import { useEffect, useRef, useState } from 'react'
import { Button } from 'primereact/button'
import { RadioButton } from 'primereact/radiobutton'

import {
    CompensatingAction,
    ConflictType,
    ResolveWorkFileConflictRequestDto,
} from '../../_generated/api/workfile-import'
import { MIN_SEARCH_LENGTH, SearchDomains, useSearchContext } from '../contexts/SearchContext'
import { useToastContext } from '../contexts/ToastContext'
import { useUserContext } from '../contexts/UserContext'
import { useWorkfileContext } from '../contexts/WorkfileContext'
import DialogContainer, { DialogContainerProps } from '../common/pure/DialogContainer'
import { Column, Row } from '../common/pure/FlexContainer'
import Heading from '../common/pure/Heading'
import { DropdownField } from '../common/pure/input-fields'
import SearchResult from '../../models/SearchResult'
import Workfile from '../../models/Workfile'
import t from '../../tools/t'

interface WorkfileConflictDialogProps extends DialogContainerProps {
    workfile: Workfile
}

interface ConflictResolutionFlowStepProps {
    step?: number
    resolution?: ResolveWorkFileConflictRequestDto
    setResolution: (resolution?: ResolveWorkFileConflictRequestDto) => void
}

export default function WorkfileConflictDialog({
    workfile,
    onHide,
    ...props
}: WorkfileConflictDialogProps) {
    // NOTE: Only the BoltConnectionNotFound flow uses stepping.
    // Instead of implementing a generic solution, we just make a quick step-hack, that will allow this easily.
    // For clarity, we only allow the steps supported by that flow, i.e. 1 and 2
    const [step, setStep] = useState<1 | 2>(1)

    const { me } = useUserContext()
    const { resolveWorkfileConflict } = useWorkfileContext()
    const conflict = workfile.conflict
    const [resolution, setResolution] = useState<ResolveWorkFileConflictRequestDto>()
    const unsetResolution = () => setResolution(undefined)

    const isAdmin = !!me?.isAdmin
    const isUseOther = resolution?.compensatingAction === CompensatingAction.UseOtherBoltConnection
    const shouldStep = isUseOther && step === 1
    const isUnknown = conflict?.type === ConflictType.UnkownConflict

    useEffect(unsetResolution, [workfile])

    const canSubmitResolution = (() => {
        switch (resolution?.compensatingAction) {
            case CompensatingAction.RestoreBoltConnection:
            case CompensatingAction.InvalidateTag:
            case CompensatingAction.MoveToEarliestAvailable:
            case CompensatingAction.MoveToEnd:
                return true
            case CompensatingAction.UseOtherBoltConnection:
                return step === 1 || !!resolution.parameters?.boltConnectionId
            default:
                return false
        }
    })()

    const stepTitle = (() => {
        switch (conflict?.type) {
            case ConflictType.DublicateTag:
                return t('workfile_conflict_duplicate_tag_title')
            case ConflictType.IndexConflict:
                return t('workfile_conflict_index_title')
            case ConflictType.BoltConnectionNotFound:
                if (step === 1) return t('workfile_conflict_bolt_connection_not_found_title')
                else return t('workfile_conflict_bolt_connection_choose_other_title')
            case ConflictType.UnkownConflict:
            default:
                return t('workfile_conflict_unknown_title')
        }
    })()

    const stepDescription = (() => {
        switch (conflict?.type) {
            case ConflictType.DublicateTag:
                if (isAdmin) return t('workfile_conflict_duplicate_tag_admin_description')
                else return t('workfile_conflict_duplicate_tag_user_description')
            case ConflictType.IndexConflict:
                return t('workfile_conflict_index_description')
            case ConflictType.BoltConnectionNotFound:
                if (step === 1) return t('workfile_conflict_bolt_connection_not_found_description')
                else return t('workfile_conflict_bolt_connection_choose_other_description')
            case ConflictType.UnkownConflict:
            default:
                return t('workfile_conflict_unknown_description')
        }
    })()

    const buttonText = (() => {
        if (shouldStep) return t('common_continue')
        if (conflict?.type === ConflictType.DublicateTag)
            return t('workfile_conflict_invalidate_tag_button')
        return t('common_submit')
    })()

    const hide = () => {
        onHide?.()
        unsetResolution()
        setStep(1)
    }

    const submit = async () => {
        if (!conflict) return
        // Step UseOtherBoltConnection if not at the end.
        if (shouldStep) return setStep(2)

        if (!resolution) return
        const result = await resolveWorkfileConflict(workfile.id, conflict.id, resolution)
        if (result) hide()
    }

    const manuallyResolve = async () => {
        if (!conflict) return
        if (!confirm(t('workfile_manual_warning'))) return
        const result = await resolveWorkfileConflict(workfile.id, conflict.id, {
            compensatingAction: CompensatingAction.ManuelResolve,
            parameters: null,
        })
        if (result) hide()
    }

    return (
        <DialogContainer {...props} className="flow-dialog" size="medium" onHide={hide}>
            <Heading className="flow-dialog__title" size={3}>
                {stepTitle}
            </Heading>
            <Column className="flow-dialog__content" gap={1}>
                <div className="flow-dialog__description">{stepDescription}</div>
                {(() => {
                    switch (conflict?.type) {
                        case ConflictType.DublicateTag:
                            return (
                                <DuplicateTagFlow
                                    resolution={resolution}
                                    setResolution={setResolution}
                                />
                            )
                        case ConflictType.IndexConflict:
                            return (
                                <IndexConflictFlow
                                    resolution={resolution}
                                    setResolution={setResolution}
                                />
                            )
                        case ConflictType.BoltConnectionNotFound:
                            return (
                                <BoltConnectionNotFoundFlow
                                    step={step}
                                    resolution={resolution}
                                    setResolution={setResolution}
                                />
                            )
                        case ConflictType.UnkownConflict:
                        default:
                            return undefined
                    }
                })()}
            </Column>
            <Row alignment="center" arrangement="spaced" wide>
                <Button onClick={hide} label={t('common_cancel')} severity="secondary" text />
                <Row alignment="center" gap={1}>
                    {isAdmin && (
                        <div className="clickable-text" onClick={manuallyResolve}>
                            {t('workfile_conflict_handle_manually')}
                        </div>
                    )}
                    {!isUnknown && (
                        <Button
                            label={buttonText}
                            disabled={!canSubmitResolution}
                            onClick={submit}
                        />
                    )}
                </Row>
            </Row>
        </DialogContainer>
    )
}

const DuplicateTagFlow = ({ setResolution }: ConflictResolutionFlowStepProps) => {
    const { me } = useUserContext()
    const isAdmin = !!me?.isAdmin

    useEffect(() => {
        if (!isAdmin) return
        setResolution({
            compensatingAction: CompensatingAction.InvalidateTag,
        })
    }, [isAdmin])

    // NOTE: Only admins can resolve this conflict and there is no action to perform.
    //  Submitting the flow at this stage just invalidates the tag.
    return <div />
}

const IndexConflictFlow = ({ setResolution }: ConflictResolutionFlowStepProps) => {
    const [action, setAction] = useState<CompensatingAction>()

    const selectAction = (compensatingAction: CompensatingAction) => {
        setAction(compensatingAction)
        setResolution({
            compensatingAction,
        })
    }

    return (
        <Column gap={0.5}>
            <Row arrangement="start" alignment="center" gap={0.5} tag="label">
                <RadioButton
                    checked={action === CompensatingAction.MoveToEarliestAvailable}
                    onChange={() => selectAction(CompensatingAction.MoveToEarliestAvailable)}
                />
                {t('workfile_conflict_index_use_lowest_available')}
            </Row>
            <Row arrangement="start" alignment="center" gap={0.5} tag="label">
                <RadioButton
                    checked={action === CompensatingAction.MoveToEnd}
                    onChange={() => selectAction(CompensatingAction.MoveToEnd)}
                />
                {t('workfile_conflict_index_add_to_end')}
            </Row>
        </Column>
    )
}

const BoltConnectionNotFoundFlow = ({
    step,
    resolution,
    setResolution,
}: ConflictResolutionFlowStepProps) => {
    const { rawSearch } = useSearchContext()
    const Toast = useToastContext()
    const latestBoltConnectionSearch = useRef(0)
    const [boltConnectionOptions, setBoltConnectionOptions] = useState<SearchResult[]>([])
    const [selectedBoltConnection, setSelectedBoltConnection] = useState<SearchResult>()
    const [boltConnectionsLoading, setBoltConnectionsLoading] = useState(false)
    const selectedBoltConnectionId = resolution?.parameters?.boltConnectionId
    const dropdownBoltConnections =
        selectedBoltConnection &&
        !boltConnectionOptions.some(({ domainId }) => domainId === selectedBoltConnection.domainId)
            ? [selectedBoltConnection, ...boltConnectionOptions]
            : boltConnectionOptions

    useEffect(() => {
        if (step === 2) return
        latestBoltConnectionSearch.current++
        setBoltConnectionOptions([])
        setSelectedBoltConnection(undefined)
        setBoltConnectionsLoading(false)
    }, [step])

    const selectRestore = () =>
        setResolution({
            compensatingAction: CompensatingAction.RestoreBoltConnection,
        })

    const selectUseOther = () =>
        setResolution({
            compensatingAction: CompensatingAction.UseOtherBoltConnection,
        })

    const selectBoltConnection = (boltConnectionId?: string) => {
        setSelectedBoltConnection(
            dropdownBoltConnections.find(({ domainId }) => domainId === boltConnectionId)
        )
        setResolution({
            compensatingAction: CompensatingAction.UseOtherBoltConnection,
            parameters: boltConnectionId ? { boltConnectionId } : undefined,
        })
    }

    const searchBoltConnections = async (query: string) => {
        const searchTerm = query.trim()
        const searchIndex = ++latestBoltConnectionSearch.current
        if (searchTerm.length < MIN_SEARCH_LENGTH) {
            setBoltConnectionOptions([])
            setBoltConnectionsLoading(false)
            return
        }

        setBoltConnectionsLoading(true)
        try {
            const results = await rawSearch({
                query: searchTerm,
                types: [SearchDomains.BoltConnection],
            })
            if (latestBoltConnectionSearch.current !== searchIndex) return
            setBoltConnectionOptions(results)
        } catch (error) {
            if (latestBoltConnectionSearch.current !== searchIndex) return
            if (error instanceof Error) Toast.error(error, t('search_error_results'))
            else Toast.error(new Error('Unknown error'), t('search_error_results'))
        } finally {
            if (latestBoltConnectionSearch.current === searchIndex) setBoltConnectionsLoading(false)
        }
    }

    switch (step) {
        case 1:
            return (
                <Column gap={0.5}>
                    <Row arrangement="start" alignment="center" gap={0.5} tag="label">
                        <RadioButton
                            checked={
                                resolution?.compensatingAction ===
                                CompensatingAction.RestoreBoltConnection
                            }
                            onChange={selectRestore}
                        />
                        {t('workfile_conflict_bolt_connection_not_found_restore')}
                    </Row>
                    <Row arrangement="start" alignment="center" gap={0.5} tag="label">
                        <RadioButton
                            checked={
                                resolution?.compensatingAction ===
                                CompensatingAction.UseOtherBoltConnection
                            }
                            onChange={selectUseOther}
                        />
                        {t('workfile_conflict_bolt_connection_not_found_choose_other')}
                    </Row>
                </Column>
            )

        case 2:
            return (
                <DropdownField
                    // We must use pass-through to avoid long names spanning outside the container.
                    pt={{ panel: { className: 'workfile-dialog-dropdown-panel' } }}
                    filter
                    loading={boltConnectionsLoading}
                    value={selectedBoltConnectionId}
                    options={dropdownBoltConnections}
                    optionLabel="name"
                    optionValue="domainId"
                    placeholder={t('common_bolt_connection')}
                    emptyMessage={t('workfile_conflict_bolt_connection_empty')}
                    resetFilterOnHide
                    onFilter={(event) => searchBoltConnections(event.filter)}
                    onChange={(event) => selectBoltConnection(event.value)}
                />
            )
    }
}
